import { colors } from '@lmc/common/constants'
import type { Resident, User } from '@lmc/ts-common'
import dayjs from 'dayjs'
import _ from 'lodash'
import { type FC, type ReactNode, useEffect, useState } from 'react'
import { postJSON } from '../../../common/services/api'
import Tour from './Tour'

const DAYS_UNTIL_TOUR_HIDDEN = 7

type Props = { user?: User; residents?: Resident[] }

const HomeTitle: FC<Props> = ({ user, residents }) => {
  const [birthdayBoysNGirls, setBirthdayBoysNGirls] = useState<
    null | Resident[]
  >()

  const [isNewHome, setIsNewHome] = useState<null | boolean>()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (residents) {
      if (!user?.firstLogin) {
        // TODO: remove this. Don't mutate the state object directly
        // @ts-ignore
        user.firstLogin = dayjs() // avoid it being triggered every time you log in.
        postJSON({ url: 'reports/user/firstlogin' })
      }

      const birthdayBoysNGirls = _.sortBy(
        residents.filter(
          (res: Resident) =>
            res.dateOfBirth &&
            dayjs(res.dateOfBirth).format('MM/DD') === dayjs().format('MM/DD')
        ),
        'name.first'
      )

      const isNewHome =
        !user?.firstLogin ||
        dayjs().diff(user.firstLogin, 'days') < DAYS_UNTIL_TOUR_HIDDEN ||
        !residents ||
        !residents.length

      setBirthdayBoysNGirls(birthdayBoysNGirls)
      setIsNewHome(isNewHome)
    }
  }, [])

  const renderBirthdays = (greeting: ReactNode, residents: Resident[]) => {
    if (residents.length === 1) {
      const age = dayjs().diff(residents[0].dateOfBirth, 'years')
      const displayName =
        residents[0]?.preferredName ||
        `${residents[0].name.first} ${residents[0].name.last}`
      return (
        <span>
          {greeting} don`t forget to say happy birthday to{' '}
          <strong>{displayName}</strong>, who`s turning {age}!
        </span>
      )
    }
    const names = residents.reduce(
      (prev: string, next: Resident, index: number) => {
        if (index === residents.length - 1) {
          return `${prev} and ${next.name.first}`
        }
        if (prev !== '') {
          return `${prev}, ${next.name.first}`
        }
        return next.name.first
      },
      ''
    )

    return (
      <span>
        {greeting} don`t forget to say happy birthday to {names}!
      </span>
    )
  }

  const renderMessages = () => {
    const userName =
      user?.name && user.name.first && user.name.first.split(' ').length > 1
        ? user?.name.first.split(' ')[0]
        : user?.name.first

    const initialGreeting = <span style={styles.bold}>Hey {userName},</span>

    if (isNewHome) {
      return (
        <div style={styles.tourIntro}>
          <p>
            {initialGreeting} This is where you manage your team, residents and
            the care provided in your home. To help you get started we`ve come
            up with a quick tour. This shouldn`t take more than a few minutes
            and at the end you`ll be a Rockstar when it comes to using it.
          </p>
          <br />
          <Tour forceOpen={!user?.firstLogin} />
        </div>
      )
    }
    if (birthdayBoysNGirls?.length) {
      return renderBirthdays(initialGreeting, birthdayBoysNGirls)
    }
    return null
  }

  if (!isNewHome && (!birthdayBoysNGirls || !birthdayBoysNGirls.length))
    return null

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>{renderMessages()}</h2>
    </div>
  )
}

const styles = {
  container: {
    padding: 0
  },
  title: {
    fontSize: 20,
    color: colors.red
  },
  bold: {
    fontWeight: 'bold'
  },
  tourIntro: {
    color: colors.darkgrey,
    fontWeight: 400,
    fontSize: 18,
    maxWidth: 800,
    lineHeight: '21px'
  }
}

export default HomeTitle
